import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, CalendarDays, MapPin, Sparkles, ArrowRight } from "lucide-react";

export default function PackageCard({ pkg, index = 0 }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: (index % 4) * 0.06 }}
      className="rounded-3xl overflow-hidden bg-white border border-slate-100 shadow-lg shadow-blue-500/5 hover:shadow-2xl hover:shadow-blue-500/15 hover:-translate-y-1 transition-transform flex flex-col"
      data-testid={`package-card-${pkg.id}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={pkg.image}
          alt={pkg.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold text-slate-900 uppercase tracking-wider inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {pkg.destination_name}
          </span>
          <span className="px-2.5 py-1 rounded-full bg-[#D52B1E] text-white text-[10px] font-bold uppercase tracking-wider">
            {pkg.tier}
          </span>
        </div>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-display font-bold text-xl text-slate-900 leading-tight mb-1">
          {pkg.name}
        </h3>
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-5">
          <CalendarDays className="w-3.5 h-3.5" />
          {pkg.duration}
        </div>

        {/* Customized pricing block */}
        <div className="mb-5 rounded-2xl bg-gradient-to-br from-[#FFF5F3] to-[#FAF6EE] border border-red-100 p-4" data-testid={`package-pricing-${pkg.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3.5 h-3.5 text-[#D52B1E]" />
            <p className="text-[11px] font-bold text-[#D52B1E] uppercase tracking-widest">Customized Pricing</p>
          </div>
          <p className="text-sm text-slate-700 leading-snug">
            Contact us for a personalized quotation based on your requirements.
          </p>
        </div>

        <ul className="space-y-2 mb-6 flex-1">
          {pkg.includes.slice(0, 8).map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
          {pkg.includes.length > 8 && (
            <li className="text-xs text-slate-400 italic pl-6">+ {pkg.includes.length - 8} more inclusions</li>
          )}
        </ul>

        <Link
          to={`/contact?package=${pkg.id}&destination=${pkg.destination_id}`}
          data-testid={`package-quote-${pkg.id}`}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full gradient-maple text-white font-semibold text-sm shadow-lg shadow-red-500/25 hover:shadow-xl hover:-translate-y-0.5 transition-transform"
        >
          Request a Quote <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.article>
  );
}
